import { useId, type ReactNode } from "react";
import { styles } from "../styles";
import { minutesToLabel } from "../lib/time";

// Labeled text input with inline error or hint, ported from the prototype.
export function Field({
  label, value, onChange, err, hint, type = "text", placeholder, autoComplete,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  err?: string;
  hint?: string;
  type?: string;
  placeholder?: string;
  autoComplete?: string;
}) {
  const id = useId();
  return (
    <div style={styles.field}>
      <label htmlFor={id} style={styles.label}>{label}</label>
      <input
        id={id} type={type} value={value} placeholder={placeholder} autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={!!err}
        aria-describedby={err ? `${id}-e` : hint ? `${id}-h` : undefined}
        className="input"
        style={{ ...styles.input, ...(err ? styles.inputErr : {}) }}
      />
      {err ? (
        <span id={`${id}-e`} style={styles.errText} role="alert">{err}</span>
      ) : hint ? (
        <span id={`${id}-h`} style={styles.hint}>{hint}</span>
      ) : null}
    </div>
  );
}

// Time-of-day select used by the admin hours editor.
export function TimeSelect({
  value, onChange, opts, label,
}: {
  value: number;
  onChange: (v: number) => void;
  opts: number[];
  label: string;
}) {
  return (
    <select
      value={value} onChange={(e) => onChange(Number(e.target.value))}
      className="input select" style={styles.select} aria-label={label}
    >
      {opts.map((t) => <option key={t} value={t}>{minutesToLabel(t)}</option>)}
    </select>
  );
}

export function Empty({ title, body }: { title: string; body: string }) {
  return (
    <div style={styles.empty}>
      <div style={styles.emptyMark} aria-hidden="true">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="9" stroke="var(--muted)" strokeWidth="1.6" />
          <path d="M12 8v5l3 2" stroke="var(--muted)" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </div>
      <p style={styles.emptyTitle}>{title}</p>
      <p style={styles.emptyBody}>{body}</p>
    </div>
  );
}

// User-visible error surface, written in the interface's own voice.
export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <div style={styles.errorBox} role="alert">
      <span aria-hidden="true">⚠</span>
      <span>{children}</span>
    </div>
  );
}
